'use client';

import { PortableText, type PortableTextComponents } from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';

import { GroqImage } from '@/helpers/Image';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { cn } from '@/lib/utils';
import ChevronCta from './ChevronCta';
import EdgePlayer from './edge/EdgePlayer';
import EdgeScene from './edge/EdgeScene';
import { RevealBlock, RevealEyebrow, RevealStyles, RevealTitle, RevealWords } from './edge/reveal';

const cellText: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className="m-0">{children}</p>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-[600]">{children}</strong>,
  },
};

export default function EdgeSection({ data }: { data: PageRizAffiliateSchema }) {
  if (
    !data?.edgeChip &&
    !data?.edgeHeading?.length &&
    !data?.edgeText &&
    !data?.edgeItems?.length &&
    !data?.edgeImage &&
    !data?.edgeCTA
  )
    return null;

  const audio = stegaClean(data?.edgePlayerAudio?.src);
  const hasScene = !!(data?.edgeImage || data?.edgeMicImage);

  return (
    <section className="bg-[#050B09] py-[8rem] lg:py-[14.875rem] [&_*]:antialiased">
      <RevealStyles />
      <div className="mx-auto grid w-full grid-cols-1 gap-y-[4rem] px-[2rem] lg:w-[min(150rem,100%)] lg:grid-cols-[1fr_auto_auto_1fr] lg:gap-x-[2rem] lg:px-0">
        {(data?.edgeChip || data?.edgeHeading?.length > 0 || data?.edgeText) && (
          <div className="flex flex-col items-center gap-[2rem] lg:col-span-4 lg:row-start-1 lg:gap-[1.5rem]">
            <RevealEyebrow text={data?.edgeChip} />
            <RevealTitle
              value={data?.edgeHeading}
              className="rz-tick-dark richtext richtext-center w-[min(41.375rem,100%)] pb-[0.5rem] [text-wrap:wrap] lg:pb-0 lg:pr-[0.375rem] prose-h2:m-0 prose-h2:font-instrument-sans prose-h2:text-[5rem] prose-h2:font-[500] prose-h2:leading-[5.5rem] prose-h2:tracking-[-0.25rem] prose-h2:text-white lg:w-full lg:prose-h2:font-geist lg:prose-h2:text-[6rem] lg:prose-h2:leading-[5.25rem] lg:prose-h2:tracking-[-0.3rem] [--hl-ctr:0px] [--hl-dy:0rem] [--hl-ext:0rem] [--hl-pl:0.919rem] [--hl-pr:0.919rem] [--hl-tracking:-0.25rem] [--hl-wash:5rem] lg:[--hl-dy:0.125rem] lg:[--hl-ext:-0.5rem] lg:[--hl-ml:0.1302em] lg:[--hl-pl:0.7875rem] lg:[--hl-pr:1.05rem] lg:[--hl-tracking:-0.3rem] lg:[--hl-wash:6.75rem] lg:[--rz-tl:0.5rem]"
            />
            <RevealWords
              text={data?.edgeText}
              className="m-0 w-[min(41.25rem,100%)] text-center font-instrument-sans text-[2rem] font-[500] leading-[3rem] text-white/[0.64] [text-wrap:wrap] lg:mt-[0.5rem] lg:w-full lg:font-geist lg:text-[2.25rem] lg:font-[400] lg:leading-[3.5rem] lg:text-white/[0.72]"
            />
          </div>
        )}

        {(hasScene || audio) && (
          <div className="grid lg:contents">
            {hasScene && (
              <div className="col-start-1 row-start-1 lg:col-span-4 lg:row-start-2">
                <EdgeScene
                  image={data?.edgeImage}
                  mic={data?.edgeMicImage}
                  sticker={data?.edgeSticker}
                />
              </div>
            )}
            {audio && (
              <RevealBlock
                className={cn(
                  'relative z-10 col-start-1 row-start-1 lg:col-start-2 lg:row-start-4',
                  hasScene && 'mx-[0.875rem] mb-[0.925rem] self-end lg:m-0 lg:self-center',
                  !data?.edgeCTA && 'lg:col-span-2 lg:justify-self-center',
                )}>
                <EdgePlayer
                  src={audio}
                  label={data?.edgePlayerLabel}
                  title={data?.edgePlayerTitle}
                />
              </RevealBlock>
            )}
          </div>
        )}

        {data?.edgeItems?.length > 0 && (
          <div className="overflow-hidden rounded-[1rem] border border-white/[0.12] bg-white/[0.04] px-[2.5rem] py-[1rem] lg:col-span-4 lg:row-start-3 lg:-mt-[0.625rem] lg:rounded-[0.5rem] lg:bg-transparent lg:p-0">
            <ul className="m-0 flex list-none flex-col p-0 lg:-m-px lg:grid lg:grid-cols-3">
              {data.edgeItems.map((item, i) => (
                <li
                  key={i}
                  className="border-b border-white/[0.08] last:border-b-0 lg:border-r lg:border-white/[0.12] lg:last:border-b">
                  <RevealBlock
                    index={i % 3}
                    className="flex items-center gap-[1.5rem] py-[2rem] lg:h-[11.875rem] lg:gap-[2rem] lg:px-[3rem] lg:py-0">
                    <span className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-[0.75rem] border border-transparent bg-[#A8E8D4]/[0.08] lg:h-[5.5rem] lg:w-[5.5rem] lg:border-[#A8E8D4]/[0.14]">
                      {item?.icon && (
                        <GroqImage
                          src={item.icon}
                          alt=""
                          quality={100}
                          style={{ width: '2.25rem' }}
                          className="!h-[2.25rem] !w-[2.25rem]"
                        />
                      )}
                    </span>
                    {item?.text?.length > 0 && (
                      <div className="min-w-0 flex-1 font-geist text-[2rem] font-[500] leading-[3rem] text-white/[0.88]">
                        <PortableText
                          value={item.text}
                          components={cellText}
                        />
                      </div>
                    )}
                  </RevealBlock>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data?.edgeCTA && (
          <RevealBlock
            index={1}
            className={cn(
              'flex justify-center lg:row-start-4 lg:col-start-3 lg:self-center',
              !audio && 'lg:col-span-2 lg:col-start-2',
            )}>
            <ChevronCta
              data={data.edgeCTA}
              language={data?.settings?.language}
            />
          </RevealBlock>
        )}
      </div>
    </section>
  );
}
